// Copyright (c) Ping. All rights reserved.
//
// In-call reaction picker — Figma DS "In call reaction menu" (889:12466):
// Emojis / Actions / Reactions tabs. Emoji reactions fly over everyone's view
// (with the sender's avatar). "Actions" holds the two persistent toggles —
// Hand up and Be Right Back — which stay on the sender's participant card.
// "Reactions" triggers full-screen celebratory effects streamed to everyone.

import { useState } from 'react';
import { Pressable, Text, View } from 'react-native';

import type { CallEffectKind } from '@calls/livekit/callData';

export interface ReactionMenuProps {
  readonly handRaised: boolean;
  readonly brb: boolean;
  readonly onReact: (emoji: string) => void;
  readonly onToggleHand: () => void;
  readonly onToggleBrb: () => void;
  readonly onEffect: (effect: CallEffectKind) => void;
  readonly onClose: () => void;
}

const EMOJIS = ['👋', '❤️', '😂', '👍', '😮', '🚀'];
const EFFECTS: ReadonlyArray<{ kind: CallEffectKind; emoji: string; label: string }> = [
  { kind: 'confetti', emoji: '🎉', label: 'Hooray!' },
  { kind: 'hearts', emoji: '💕', label: 'Love it' },
  { kind: 'fireworks', emoji: '🎆', label: 'Celebrate' },
];

type Tab = 'emojis' | 'actions' | 'reactions';

export function ReactionMenu({
  handRaised,
  brb,
  onReact,
  onToggleHand,
  onToggleBrb,
  onEffect,
  onClose,
}: ReactionMenuProps): JSX.Element {
  const [tab, setTab] = useState<Tab>('emojis');

  const react = (emoji: string) => {
    onReact(emoji);
    onClose();
  };

  return (
    <View className="rounded-[12px] border border-[#E1E3E8] dark:border-[#2F333B] bg-white dark:bg-[#15171C] p-[8px]">
      {/* Tabs */}
      <View className="mb-[8px] flex-row gap-[16px] px-[4px]">
        {(['emojis', 'actions', 'reactions'] as const).map((t) => (
          <Pressable key={t} onPress={() => setTab(t)}>
            <Text
              className={`text-[14px] ${tab === t ? 'font-bold text-[#15171C] dark:text-[#F5F6F8]' : 'text-[#6B7280] dark:text-[#B5BAC3]'}`}
            >
              {t === 'emojis' ? 'Emojis' : t === 'actions' ? 'Actions' : 'Reactions'}
            </Text>
          </Pressable>
        ))}
      </View>

      {tab === 'emojis' ? (
        <View className="flex-row gap-[8px]">
          {EMOJIS.map((e) => (
            <Pressable
              key={e}
              accessibilityLabel={`React ${e}`}
              onPress={() => react(e)}
              className="h-[34px] w-[34px] items-center justify-center rounded-[8px] active:bg-black/[0.06] dark:active:bg-white/[0.08]"
            >
              <Text className="text-[18px]">{e}</Text>
            </Pressable>
          ))}
        </View>
      ) : null}

      {tab === 'actions' ? (
        <View className="flex-row gap-[8px]">
          <Pressable
            onPress={() => {
              onToggleHand();
              onClose();
            }}
            className={`h-[28px] flex-row items-center gap-[6px] rounded-[8px] border px-[10px] ${
              handRaised ? 'border-[#FFCB45] bg-[#FFCB45]/10' : 'border-[#E1E3E8] dark:border-[#222429]'
            }`}
          >
            <Text className="text-[14px]">✋</Text>
            <Text className="text-[14px] text-[#15171C] dark:text-white">{handRaised ? 'Lower hand' : 'Hand up'}</Text>
          </Pressable>
          <Pressable
            onPress={() => {
              onToggleBrb();
              onClose();
            }}
            className={`h-[28px] flex-row items-center gap-[6px] rounded-[8px] border px-[10px] ${
              brb ? 'border-[#FFCB45] bg-[#FFCB45]/10' : 'border-[#E1E3E8] dark:border-[#222429]'
            }`}
          >
            <Text className="text-[14px]">☕️</Text>
            <Text className="text-[14px] text-[#15171C] dark:text-white">{brb ? 'I’m back' : 'Be Right Back'}</Text>
          </Pressable>
        </View>
      ) : null}

      {tab === 'reactions' ? (
        <View className="flex-row gap-[8px]">
          {EFFECTS.map((r) => (
            <Pressable
              key={r.kind}
              accessibilityLabel={r.label}
              onPress={() => {
                onEffect(r.kind);
                onClose();
              }}
              className="h-[32px] flex-row items-center gap-[6px] rounded-[8px] border border-[#E1E3E8] dark:border-[#222429] px-[10px]"
            >
              <Text className="text-[14px]">{r.emoji}</Text>
              <Text className="text-[14px] text-[#15171C] dark:text-white">{r.label}</Text>
            </Pressable>
          ))}
        </View>
      ) : null}
    </View>
  );
}
